import { createAsyncThunk } from '@reduxjs/toolkit';

import { SESSION_MANAGER_HOST } from '@/constants';
import { store } from '@/Infrastructure/State';
import type { getTokenUserIdResponse } from '@/modules/log/domain/Response';
import type { TokenUserIdRepository } from '@/modules/log/domain/TokenUserIdRepository';

export interface GuestSession {
  anonymousId: string;
  tokenUserId?: string;
}

interface GetParams {
  url: string;
  storageUrl: string;
  options?: RequestInit;
}

const authGet = async <Payload>({ url }: GetParams): Promise<Payload> => {
  try {
    const response = await fetch(url, {
      credentials: 'include',
      cache: 'no-cache'
    });
    const responseData = await response.json();

    if (!response.ok) {
      throw {
        ...responseData,
        status: response.status
      };
    }
    return responseData;
  } catch (e) {
    const error = e as { statusCode: number };
    error.statusCode = error.statusCode || 403;
    throw error;
  }
};

const getTokenUserId = async (): Promise<getTokenUserIdResponse> => {
  try {
    const response: {
      code: number;
      data: { tokenUserId: string };
    } = await authGet({
      url: `${SESSION_MANAGER_HOST}/token-user-id`,
      storageUrl: SESSION_MANAGER_HOST
    });
    return {
      code: 200,
      tokenUserId: response?.data?.tokenUserId
    };
  } catch (error) {
    console.error('Error during get token user id:', error);
    throw error;
  }
};

export const createTokenUserIdRepository = (): TokenUserIdRepository => ({
  getTokenUserId: async () => {
    const { payload } = await store.dispatch(getTokenUserIdAsync());
    return payload as getTokenUserIdResponse;
  }
});

export const getTokenUserIdAsync = createAsyncThunk<
  getTokenUserIdResponse,
  void,
  { rejectValue: { message: string; status: number } }
>('log/getTokenUserId', async (_, { rejectWithValue }) => {
  try {
    const response = await getTokenUserId();
    return response;
  } catch (error) {
    return rejectWithValue({
      message: 'Failed to fetch token user RUT',
      status: 500
    });
  }
});
